import { defineStore } from 'pinia'
import { ref, type Ref } from 'vue'
import { useSelectedPlanStore, type PlanStore } from '@/stores/plans/selectedPlan.ts'
import { getUsersAccess } from '../../services/api/users/users.ts'

export const useAccessRightsStore = defineStore(
	'accessRights',
	() => {
		// ====---------------====
		// 🏪 Stores
		// ====---------------====
		const planStore = useSelectedPlanStore().getStore()

		// ====---------------====
		// 🗄️ State
		// ====---------------====
		const state = ref<RightsObject | null>(null)
		const loaded = ref<boolean>(false)

		// ====---------------====
		// 🏃‍♂️ Getters
		// ====---------------====
		async function loadStore(plan: PlanStore | null = null): Promise<RightsObject | null> {
			if (!loaded.value) await loadAccessRightsStore(plan)

			return getStore()
		}

		function getStore(): RightsObject | null {
			return state.value
		}

		function getAccessibleIdsArray(): Array<string> {
			const memberList = state.value?.accessibleMembers.map((x) => {
				return x.memberId
			})
			return memberList ?? []
		}

		function getAccessibleIdsString() {
			return getAccessibleIdsArray()?.join() ?? null
		}

		function getLoaded(): Ref<boolean> {
			return loaded
		}

		// ====---------------====
		// 🎬 Actions
		// ====---------------====
		function $reset() {
			state.value = null
			loaded.value = false
		}

		let loadAccessRightsStoreRequest: Promise<string> | null = null
		const loadAccessRightsStore = async (plan: PlanStore | null = null) => {
			if (loadAccessRightsStoreRequest) {
				return loadAccessRightsStoreRequest
			}
			loadAccessRightsStoreRequest = new Promise(async (resolve, reject) => {
				const planData = plan ? plan : planStore
				if (!planData) {
					resolve('')
					return
				}

				try {
					await getUsersAccess(
						planData.username,
						planData.companyId,
						planData.memberId,
						planData.contractType
					)
						.then((res: RightsObject) => {
							loaded.value = true
							state.value = res
						})
						.catch((err: string) => {
							console.error(err)
						})

					resolve('')
				} catch (err) {
					reject(err)
				} finally {
					loadAccessRightsStoreRequest = null
				}
			})
			return loadAccessRightsStoreRequest
		}

		// ====---------------====
		// 🚀 Exports
		// ====---------------====
		return {
			state,
			loaded,
			getLoaded,
			loadStore,
			getStore,
			getAccessibleIdsArray,
			getAccessibleIdsString,
			$reset
		}
	},
	{
		persist: {
			storage: sessionStorage
		}
	}
)
